import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { gmailService } from '../services/gmailService'
import { message } from 'antd'

export function useEmailThread(threadId: string | undefined, senderEmail?: string) {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['email-thread', threadId, senderEmail],
    queryFn: () => gmailService.getThread(threadId!, senderEmail),
    enabled: !!threadId,
  })

  const sendMutation = useMutation({
    mutationFn: (data: {
      to: string
      subject: string
      body: string
      threadId?: string
      inReplyTo?: string
    }) => gmailService.sendEmail(data),
    onSuccess: () => {
      message.success('Email sent successfully!')
      queryClient.invalidateQueries({ queryKey: ['email-thread', threadId] })
      queryClient.invalidateQueries({ queryKey: ['emails'] })
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to send email')
    },
  })

  const markAsReadMutation = useMutation({
    mutationFn: (read: boolean) => gmailService.markAsRead(threadId!, read),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-thread', threadId] })
      queryClient.invalidateQueries({ queryKey: ['emails'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => gmailService.deleteThread(threadId!),
    onSuccess: () => {
      message.success('Email moved to trash')
      queryClient.invalidateQueries({ queryKey: ['emails'] })
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to delete email')
    },
  })

  const labelMutation = useMutation({
    mutationFn: (labels: { add?: string[]; remove?: string[] }) =>
      gmailService.updateLabels(threadId!, labels),
    onSuccess: () => {
      message.success('Labels updated')
      queryClient.invalidateQueries({ queryKey: ['email-thread', threadId] })
      queryClient.invalidateQueries({ queryKey: ['emails'] })
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to update labels')
    },
  })

  return {
    thread: query.data?.metadata,
    messages: query.data?.messages || [],
    isLoading: query.isLoading,
    error: query.error,
    sendEmail: sendMutation.mutate,
    isSending: sendMutation.isPending,
    markAsRead: (read: boolean) => markAsReadMutation.mutate(read),
    deleteThread: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
    updateLabels: labelMutation.mutate,
    isUpdatingLabels: labelMutation.isPending,
  }
}

